import { fetchJSON as fetchAPI } from './client';

export type PhilosophyPrinciple = {
  key: string;
  name: string;
  description?: string;
  priority?: string;
};

export type MonorepoArea = {
  key: string;
  path: string;
  purpose: string;
  owner?: string;
  contains?: string[];
  mandatory: boolean;
};

export type ApplicationLayout = {
  key: string;
  name: string;
  path: string;
  description?: string;
  structure?: string[];
  status: string;
};

export type BackendCapability = {
  key: string;
  name: string;
  service_path?: string;
  pkg_path?: string;
  owns?: string[];
  status: string;
};

export type SharedPackage = {
  key: string;
  name: string;
  path: string;
  description?: string;
  category: string;
};

export type DependencyRule = {
  key: string;
  from_layer: string;
  to_layer: string;
  allowed: boolean;
  description?: string;
  examples?: string[];
};

export type BuildTool = {
  key: string;
  name: string;
  tool?: string;
  stage: string;
  description?: string;
  blocking: boolean;
};

export type EnvironmentProfile = {
  key: string;
  name: string;
  purpose: string;
  config_path?: string;
  properties?: string[];
};

export type TestingLayout = {
  key: string;
  name: string;
  test_type: string;
  location: string;
  description?: string;
  tools?: string[];
};

export type DocumentationArea = {
  key: string;
  name: string;
  path: string;
  description?: string;
  audience?: string;
};

export type BibleDashboard = {
  bible_id: string;
  tenant_id: string;
  version: string;
  applies_to: string[];
  philosophy_principles: number;
  monorepo_areas: number;
  applications: number;
  backend_capabilities: number;
  shared_packages: number;
  dependency_rules: number;
  build_tools: number;
  environments: number;
  testing_layouts: number;
  documentation_areas: number;
  generated_at: string;
};

export type BibleValidation = {
  valid: boolean;
  philosophy_defined: boolean;
  monorepo_structure_defined: boolean;
  applications_defined: boolean;
  backend_capabilities_defined: boolean;
  shared_packages_defined: boolean;
  dependency_rules_defined: boolean;
  build_tooling_defined: boolean;
  environments_defined: boolean;
  testing_layout_defined: boolean;
  documentation_defined: boolean;
  acceptance_passed: boolean;
  gaps?: string[];
  evaluated_at: string;
};

export type RABBibleStatus = {
  bible_id: string;
  version: string;
  applies_to: string[];
  philosophy_principles: number;
  monorepo_areas: number;
  applications: number;
  backend_capabilities: number;
  shared_packages: number;
  dependency_rules: number;
  build_tools: number;
  environments: number;
  testing_layouts: number;
  documentation_areas: number;
  decade_scalable: boolean;
  production_ready: boolean;
  status: string;
};

const GOVERNANCE_BASE = import.meta.env.VITE_GOVERNANCE_API ?? '/api/v1/governance';

function fetchGovernance<T>(path: string, init?: RequestInit): Promise<T> {
  return fetchAPI<T>(`${GOVERNANCE_BASE}${path}`, init);
}

export const repositoryArchitectureBibleApi = {
  dashboard: () => fetchGovernance<BibleDashboard>('/rab/dashboard'),
  philosophy: () => fetchGovernance<{ principles: PhilosophyPrinciple[] }>('/rab/philosophy').then((r) => r.principles),
  monorepoStructure: () => fetchGovernance<{ areas: MonorepoArea[] }>('/rab/monorepo-structure').then((r) => r.areas),
  applications: () => fetchGovernance<{ applications: ApplicationLayout[] }>('/rab/applications').then((r) => r.applications),
  backendCapabilities: () => fetchGovernance<{ capabilities: BackendCapability[] }>('/rab/backend-capabilities').then((r) => r.capabilities),
  sharedPackages: () => fetchGovernance<{ packages: SharedPackage[] }>('/rab/shared-packages').then((r) => r.packages),
  dependencyRules: () => fetchGovernance<{ rules: DependencyRule[] }>('/rab/dependency-rules').then((r) => r.rules),
  buildTooling: () => fetchGovernance<{ tools: BuildTool[] }>('/rab/build-tooling').then((r) => r.tools),
  environments: () => fetchGovernance<{ environments: EnvironmentProfile[] }>('/rab/environments').then((r) => r.environments),
  testingLayout: () => fetchGovernance<{ layouts: TestingLayout[] }>('/rab/testing-layout').then((r) => r.layouts),
  documentation: () => fetchGovernance<{ areas: DocumentationArea[] }>('/rab/documentation').then((r) => r.areas),
  validate: (attrs: Record<string, boolean>) =>
    fetchGovernance<BibleValidation>('/rab/validate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attrs }),
    }),
  status: () => fetchGovernance<RABBibleStatus>('/rab/status'),
};
